// Copyright © 2026 Qcells. All rights reserved.
//
// This software is proprietary and confidential. Unauthorized use,
// duplication, or distribution of software is strictly prohibited.

import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useApprovals } from '../../hooks/useApprovals';
import type { HitlApproval, HitlApprovalState } from '../../types';
import { PageHead, EmptyState, Loading, DxTag } from '../../components/shell/ScreenHelpers';
import { HITL_SITE_ID, HITL_DC_SITE_LABEL, HITL_APPROVAL_TYPE } from '../../utils/constants';

const STATUS_LABELS: Record<HitlApprovalState, string> = {
  PENDING: 'Pending review',
  APPROVED: 'Approved',
  REJECTED: 'Rejected',
};

const STATUS_BORDER_COLOR: Record<HitlApprovalState, string> = {
  PENDING: 'var(--interactive)',
  APPROVED: 'var(--st-ok)',
  REJECTED: 'var(--sev1)',
};

function approvalAttr(approval: HitlApproval, key: string): string | undefined {
  const value = approval.attributes?.[key];
  return typeof value === 'string' ? value : undefined;
}

export default function OptimizationsScreen({ review = false }: { review?: boolean }) {
  const navigate = useNavigate();

  const { approvals, loading, error } = useApprovals({
    siteId: HITL_SITE_ID,
    approvalType: HITL_APPROVAL_TYPE,
  });

  return (
    <div className="hs-page">
      <PageHead
        title={review ? 'Optimization plan review' : 'Optimizations'}
        dateline="Plan approvals from HITL"
      />

      {loading ? (
        <Loading />
      ) : error ? (
        <EmptyState big="Couldn't load optimizations." sub={error} />
      ) : approvals.length === 0 ? (
        <EmptyState
          big="No optimization plans waiting."
          sub="Plan approvals for this site appear here."
        />
      ) : (
        <div className="hs-tiles">
          {approvals.map(approval => {
            const title = approvalAttr(approval, 'title') ?? 'Optimization plan';
            const summary = approvalAttr(approval, 'summary');
            const scenarioId = approvalAttr(approval, 'scenarioId');
            // Open the plan detail, scoped to the Optimizations section (not Site).
            // The user reviews (Approve/Reject) inside the detail — there are no
            // accept/decline shortcuts on the list itself.
            const openDetail = () => navigate(`/optimizations/${approval.approvalId}`);
            return (
              <div
                key={approval.approvalId}
                className="hs-card"
                role="button"
                tabIndex={0}
                onClick={openDetail}
                onKeyDown={e => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    openDetail();
                  }
                }}
                style={{ borderLeftColor: STATUS_BORDER_COLOR[approval.state] }}
              >
                <div className="hs-card-row">
                  <DxTag>Optimization</DxTag>
                  <span
                    style={{
                      marginLeft: 'auto',
                      fontSize: 11,
                      color: 'var(--text-helper)',
                      fontFamily: 'IBM Plex Mono, monospace',
                    }}
                  >
                    {STATUS_LABELS[approval.state] ?? approval.state}
                  </span>
                </div>

                <div className="hs-card-asset">{title}</div>
                <div className="hs-card-summary">{summary ?? `Plan for ${HITL_DC_SITE_LABEL}`}</div>

                <div className="hs-card-meta">
                  {scenarioId && <span>{scenarioId}</span>}
                  {approval.createdAt && (
                    <span>· created {new Date(approval.createdAt).toLocaleString()}</span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
